import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { Plus, Search, X } from 'lucide-react'

import type { Customer } from '@/lib/api/customer-api'
import { useInfiniteCustomersQuery } from '@/hooks/use-customer-query'
import { CustomerForm } from '@/components/forms/customer-form'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

function useDebounce(value: string, delay: number) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

interface Props {
    value: Customer | null
    onChange: (customer: Customer | null) => void
}

export interface PosCustomerSearchHandle {
    focus: () => void
}

export const PosCustomerSearch = forwardRef<PosCustomerSearchHandle, Props>(
    function PosCustomerSearch({ value, onChange }, ref) {
        const [search, setSearch] = useState('')
        const [open, setOpen] = useState(false)
        const [dialogOpen, setDialogOpen] = useState(false)
        const inputRef = useRef<HTMLInputElement>(null)
        const wrapperRef = useRef<HTMLDivElement>(null)
        const scrollContainerRef = useRef<HTMLDivElement>(null)
        const sentinelRef = useRef<HTMLDivElement>(null)
        const debouncedSearch = useDebounce(search, 300)

        useImperativeHandle(ref, () => ({
            focus: () => inputRef.current?.focus(),
        }))

        const {
            data,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            isFetching,
        } = useInfiniteCustomersQuery(debouncedSearch)

        const customers = data?.pages.flatMap((p) => p.items) ?? []
        const isEmpty =
            !isFetching &&
            data !== undefined &&
            data.pages[0]?.items.length === 0

        // IntersectionObserver: load next page when sentinel enters the scroll container viewport
        const handleIntersect = useCallback(
            (entries: IntersectionObserverEntry[]) => {
                if (
                    entries[0]?.isIntersecting &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    void fetchNextPage()
                }
            },
            [fetchNextPage, hasNextPage, isFetchingNextPage],
        )

        useEffect(() => {
            const sentinel = sentinelRef.current
            const root = scrollContainerRef.current
            if (!sentinel) return

            const observer = new IntersectionObserver(handleIntersect, {
                root,
                threshold: 0.1,
            })
            observer.observe(sentinel)
            return () => observer.disconnect()
            // customers.length triggers re-create after the dropdown's
            // conditional sentinel renders; without it the first page never
            // loads page 2.
        }, [handleIntersect, open, customers.length])

        // Close dropdown on outside click
        useEffect(() => {
            const handler = (e: MouseEvent) => {
                if (
                    wrapperRef.current &&
                    !wrapperRef.current.contains(e.target as Node)
                ) {
                    setOpen(false)
                }
            }
            document.addEventListener('mousedown', handler)
            return () => document.removeEventListener('mousedown', handler)
        }, [])

        if (value) {
            return (
                <div className="flex items-center gap-2 rounded border bg-muted/50 px-2 py-1.5 text-sm">
                    <div className="flex-1 min-w-0 truncate">
                        <span className="font-medium">{value.name}</span>
                        {value.phone && (
                            <span className="text-muted-foreground ml-1.5">
                                {value.phone}
                            </span>
                        )}
                        {value.customer_group && (
                            <span className="ml-1.5 inline-flex items-center rounded bg-primary/10 text-primary px-1.5 py-0.5 text-xs font-medium">
                                {value.customer_group.name}
                                {value.customer_group.discount_percent > 0 && (
                                    <span className="ml-0.5">
                                        -{value.customer_group.discount_percent}
                                        %
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                    >
                        <X className="size-3.5" />
                    </button>
                </div>
            )
        }

        return (
            <>
                <div ref={wrapperRef} className="relative">
                    <div className="flex gap-1">
                        <div className="relative flex-1">
                            <Search className="absolute left-2 top-2 size-3.5 text-muted-foreground" />
                            <input
                                ref={inputRef}
                                placeholder={m.pos_customer_search_placeholder()}
                                className="w-full rounded border bg-background pl-7 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value)
                                    setOpen(true)
                                }}
                                onFocus={() => setOpen(true)}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => setDialogOpen(true)}
                            className="shrink-0 rounded border bg-background px-2 py-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                            title={m.customer_add_new()}
                        >
                            <Plus className="size-3.5" />
                        </button>
                    </div>
                    {open && customers.length > 0 && (
                        <div
                            ref={scrollContainerRef}
                            className="absolute z-50 mt-1 w-full rounded border bg-popover shadow-lg max-h-48 overflow-auto"
                        >
                            {customers.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => {
                                        onChange(c)
                                        setSearch('')
                                        setOpen(false)
                                    }}
                                    className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent border-b last:border-0"
                                >
                                    <span className="font-medium">
                                        {c.name}
                                    </span>
                                    <span className="text-muted-foreground ml-2">
                                        {c.phone ?? ''}
                                    </span>
                                    {c.customer_group && (
                                        <span className="ml-2 text-xs text-primary">
                                            [{c.customer_group.name}]
                                        </span>
                                    )}
                                </button>
                            ))}
                            {isFetchingNextPage && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                                    {m.customer_loading_more()}
                                </div>
                            )}
                            <div ref={sentinelRef} className="h-px" />
                        </div>
                    )}
                    {open && isEmpty && (
                        <div className="absolute z-50 mt-1 w-full rounded border bg-popover shadow-lg px-2 py-1.5 text-sm text-muted-foreground">
                            {m.customer_not_found()}
                        </div>
                    )}
                </div>

                {/* New customer dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{m.customer_add_new()}</DialogTitle>
                            <DialogDescription>
                                {m.customer_create_for_order()}
                            </DialogDescription>
                        </DialogHeader>
                        <CustomerForm
                            onCreated={(customer) => {
                                onChange(customer)
                                setDialogOpen(false)
                            }}
                            onSuccess={() => setDialogOpen(false)}
                            onCancel={() => setDialogOpen(false)}
                        />
                    </DialogContent>
                </Dialog>
            </>
        )
    },
)
